<script>
import Layout from "../../layouts/main";
import PageHeader from "../../components/page-header";
import Swal from "sweetalert2";

/**
 * Componente para actualizar colegios desde Excel
 */
export default {
    components: { Layout, PageHeader },
    data() {
        return {
            title: "Actualizar Colegios",
            items: [
                {
                    text: "Actualización",
                    href: "/web/actualizacion"
                },
                {
                    text: "Colegios",
                    active: true
                }
            ],
            archivos: [],
            cargando: false,
            resultados: null,
            estadisticas: {
                total: 0,
                actualizados: 0,
                creados: 0,
                errores: 0,
                sin_cambios: 0
            }
        };
    },
    methods: {
        onFileChange(e) {
            this.archivos = e.target.files;
        },
        async subirArchivo() {
            if (!this.archivos || this.archivos.length === 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Selecciona un archivo',
                    text: 'Por favor selecciona un archivo Excel (.xlsx o .csv)',
                    showConfirmButton: true
                });
                return;
            }

            const archivo = this.archivos[0];

            // Validar extensión
            const extensionesValidas = ['.xlsx', '.xls', '.csv'];
            const nombreArchivo = archivo.name.toLowerCase();
            const validar = extensionesValidas.some(ext => nombreArchivo.endsWith(ext));

            if (!validar) {
                Swal.fire({
                    icon: 'error',
                    title: 'Formato inválido',
                    text: 'Solo se aceptan archivos .xlsx, .xls o .csv',
                    showConfirmButton: true
                });
                return;
            }

            // Validar tamaño (máximo 10MB)
            if (archivo.size > 10 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'Archivo muy grande',
                    text: 'El archivo no debe exceder 10MB',
                    showConfirmButton: true
                });
                return;
            }

            this.cargando = true;
            this.resultados = null;

            // Mostrar progreso
            Swal.fire({
                title: 'Procesando archivo...',
                text: 'Por favor espera mientras se procesan los datos',
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });

            const formData = new FormData();
            formData.append('archivo', archivo);

            try {
                const response = await axios.post('/api/actualizar_colegios_excel', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                this.cargando = false;

                if (response.data.success) {
                    this.resultados = response.data;
                    this.estadisticas = {
                        total: response.data.total || 0,
                        actualizados: response.data.actualizados || 0,
                        creados: response.data.creados || 0,
                        errores: response.data.errores || 0,
                        sin_cambios: (response.data.total || 0) - (response.data.actualizados || 0) - (response.data.creados || 0) - (response.data.errores || 0)
                    };

                    Swal.fire({
                        icon: 'success',
                        title: 'Actualización completada',
                        html: `
                            <div class="text-left">
                                <p><strong>Total procesados:</strong> ${this.estadisticas.total}</p>
                                <p><strong>Actualizados:</strong> ${this.estadisticas.actualizados}</p>
                                <p><strong>Creados:</strong> ${this.estadisticas.creados}</p>
                                <p><strong>Sin cambios:</strong> ${this.estadisticas.sin_cambios}</p>
                                <p><strong>Errores:</strong> ${this.estadisticas.errores}</p>
                            </div>
                        `,
                        showConfirmButton: true
                    });

                    // Limpiar input
                    this.$refs.inputArchivo.value = '';
                    this.archivos = [];
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error en el procesamiento',
                        text: response.data.message || 'Ocurrió un error al procesar el archivo',
                        showConfirmButton: true
                    });
                }
            } catch (error) {
                this.cargando = false;
                console.error('Error:', error);

                Swal.fire({
                    icon: 'error',
                    title: 'Error en la carga',
                    text: error.response?.data?.message || error.message || 'Error desconocido',
                    showConfirmButton: true
                });
            }
        },
        async descargarPlantilla() {
            return this.runDownload('plantilla_colegios', async () => {
                const headers = [
                    'rbd', 'nombre', 'colegio_visitado', 'telefono_colegio', 'direccion',
                    'region', 'comuna', 'provincia', 'telefono', 'correo_colegio',
                    'correo_colegio_2', 'estado_colegio', 'tipo_educacion', 'nivel_socioeconomico',
                    'nombre_director', 'correo_director', 'telefono_director',
                    'nombre_orientador', 'correo_orientador', 'telefono_orientador',
                    'cantidad_encuestas', 'sede_asociada', 'propietario', 'campus', 'revisado', 'valido'
                ];

                const csv = headers.join(',') + '\n';
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);

                link.setAttribute('href', url);
                link.setAttribute('download', 'plantilla_colegios.csv');
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, {
                message: 'Se esta preparando la plantilla. La descarga iniciara en unos segundos.'
            });
        }
    }
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header bg-primary text-white">
                        <h5 class="mb-0 text-white">Actualizar Información de Colegios</h5>
                    </div>
                    <div class="card-body">
                        <div class="alert alert-info">
                            <h6>📋 Instrucciones:</h6>
                            <ul class="mb-0">
                                <li>Descarga la plantilla y completa los datos</li>
                                <li>El campo <strong>RBD</strong> es obligatorio para identificar el colegio</li>
                                <li>Solo se actualizarán los campos que tengan datos en tu archivo</li>
                                <li>Soporta formatos: .xlsx, .xls y .csv</li>
                                <li>Tamaño máximo: 10MB</li>
                            </ul>
                        </div>

                        <div class="row">
                            <div class="col-md-6">
                                <div class="form-group">
                                    <label for="archivo" class="font-weight-bold">Selecciona archivo Excel</label>
                                    <div class="custom-file">
                                        <input
                                            type="file"
                                            class="custom-file-input"
                                            id="archivo"
                                            ref="inputArchivo"
                                            @change="onFileChange"
                                            accept=".xlsx,.xls,.csv"
                                            lang="es"
                                        >
                                        <label class="custom-file-label" for="archivo">
                                            {{ archivos.length > 0 ? archivos[0].name : 'Elige un archivo...' }}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row mt-3">
                            <div class="col-md-6">
                                <b-button
                                    variant="success"
                                    @click="subirArchivo"
                                    :disabled="cargando || archivos.length === 0"
                                    class="mr-2"
                                >
                                    <i class="bx bx-upload mr-2"></i>
                                    {{ cargando ? 'Procesando...' : 'Cargar y Actualizar' }}
                                </b-button>
                                <b-button
                                    variant="secondary"
                                    @click="descargarPlantilla"
                                    :disabled="isDownloadLoading('plantilla_colegios')"
                                >
                                    <b-spinner small class="mr-2" v-if="isDownloadLoading('plantilla_colegios')"></b-spinner>
                                    <i class="bx bx-download mr-2" v-else></i>
                                    {{ getDownloadLabel('plantilla_colegios', 'Descargar Plantilla', 'Preparando Plantilla...') }}
                                </b-button>
                            </div>
                        </div>

                        <!-- Resultados -->
                        <div v-if="resultados" class="row mt-4">
                            <div class="col-12">
                                <h6 class="mb-3">📊 Resumen de la actualización:</h6>
                                <div class="row">
                                    <div class="col-md-3">
                                        <div class="card border-0 bg-light">
                                            <div class="card-body text-center">
                                                <h3 class="text-primary">{{ estadisticas.total }}</h3>
                                                <p class="mb-0">Total procesados</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-3">
                                        <div class="card border-0 bg-light">
                                            <div class="card-body text-center">
                                                <h3 class="text-success">{{ estadisticas.actualizados }}</h3>
                                                <p class="mb-0">Actualizados</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-3">
                                        <div class="card border-0 bg-light">
                                            <div class="card-body text-center">
                                                <h3 class="text-info">{{ estadisticas.creados }}</h3>
                                                <p class="mb-0">Creados</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-3">
                                        <div class="card border-0 bg-light">
                                            <div class="card-body text-center">
                                                <h3 class="text-danger">{{ estadisticas.errores }}</h3>
                                                <p class="mb-0">Errores</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Detalle de errores -->
                        <div v-if="resultados && resultados.detalles_errores && resultados.detalles_errores.length > 0" class="row mt-3">
                            <div class="col-12">
                                <h6 class="mb-2 text-danger">⚠️ Registros con error:</h6>
                                <div class="table-responsive">
                                    <table class="table table-sm table-bordered">
                                        <thead class="bg-danger text-white">
                                            <tr>
                                                <th>RBD</th>
                                                <th>Fila</th>
                                                <th>Error</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(error, idx) in resultados.detalles_errores.slice(0, 10)" :key="idx">
                                                <td>{{ error.rbd }}</td>
                                                <td>{{ error.fila }}</td>
                                                <td>{{ error.error }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <p v-if="resultados.detalles_errores.length > 10" class="text-muted small">
                                        Y {{ resultados.detalles_errores.length - 10 }} errores más...
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<style scoped>
/* Personalizar el estilo del input file */
.custom-file-input ~ .custom-file-label::after {
    content: "Examinar";
}
</style>
